import { Injectable, signal } from '@angular/core';

/**
 * Thin wrapper over MediaRecorder for recording a voice note. Keeps the browser-API state machine
 * out of components: start() begins capture, stop() resolves the recorded audio as a File.
 */
@Injectable({ providedIn: 'root' })
export class VoiceRecorderService {
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private resolveStop: ((file: File | null) => void) | null = null;

  readonly isRecording = signal(false);
  readonly isSupported = typeof navigator !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia) && typeof MediaRecorder !== 'undefined';

  async start(): Promise<boolean> {
    if (!this.isSupported || this.recorder) {
      return false;
    }
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    // Prefer a format the provider (Meta/WhatsApp) accepts directly; Chrome only offers webm,
    // which the backend transcodes to ogg/opus before dispatch.
    const format = pickAudioFormat();
    const recorder = new MediaRecorder(stream, format.recorderMime ? { mimeType: format.recorderMime } : undefined);
    this.chunks = [];
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        this.chunks.push(event.data);
      }
    };
    recorder.onstop = () => {
      stream.getTracks().forEach((track) => track.stop());
      this.isRecording.set(false);
      // Store under the base content type (no ;codecs=…) so it matches the upload allowlist.
      const blob = new Blob(this.chunks, { type: format.contentType });
      const file = blob.size > 0 ? new File([blob], `mensagem-de-voz.${format.extension}`, { type: format.contentType }) : null;
      this.recorder = null;
      this.resolveStop?.(file);
      this.resolveStop = null;
    };
    this.recorder = recorder;
    recorder.start();
    this.isRecording.set(true);
    return true;
  }

  /** Stops recording and resolves with the captured audio File (or null if empty). */
  stop(): Promise<File | null> {
    if (!this.recorder) {
      return Promise.resolve(null);
    }
    return new Promise<File | null>((resolve) => {
      this.resolveStop = resolve;
      this.recorder?.stop();
    });
  }
}

interface AudioFormat {
  /** Full type (may include `;codecs=…`) passed to MediaRecorder; null lets the browser decide. */
  recorderMime: string | null;
  /** Base MIME stored on the File — must match the upload allowlist (no codec suffix). */
  contentType: string;
  extension: string;
}

// Preference order: ogg/opus (Firefox; Meta accepts it directly), otherwise webm/opus (Chrome),
// which the backend transcodes to ogg before dispatch. audio/mp4 is deliberately excluded —
// Chrome's MediaRecorder mp4 output is a fragmented container WhatsApp rejects as
// application/octet-stream on processing.
const AUDIO_FORMAT_CANDIDATES: readonly AudioFormat[] = [
  { recorderMime: 'audio/ogg;codecs=opus', contentType: 'audio/ogg', extension: 'ogg' },
  { recorderMime: 'audio/webm;codecs=opus', contentType: 'audio/webm', extension: 'webm' },
  { recorderMime: 'audio/webm', contentType: 'audio/webm', extension: 'webm' },
];

function pickAudioFormat(): AudioFormat {
  const supports = typeof MediaRecorder !== 'undefined' && typeof MediaRecorder.isTypeSupported === 'function';
  if (supports) {
    for (const candidate of AUDIO_FORMAT_CANDIDATES) {
      if (candidate.recorderMime && MediaRecorder.isTypeSupported(candidate.recorderMime)) {
        return candidate;
      }
    }
  }
  // Let the browser choose its default container; backend transcodes if the provider rejects it.
  return { recorderMime: null, contentType: 'audio/webm', extension: 'webm' };
}
